import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Sparkles, Send, Loader2, X, AlertTriangle, MessageCircle, Lock,
    Maximize2, Minimize2, RefreshCw, History,
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const STORAGE_SESSION_KEY = 'homly_seller_chat_session';

export default function SellerAIChat({ mandateId = null, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    const [fullscreen, setFullscreen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_SESSION_KEY) || null);
    const [quotaRemaining, setQuotaRemaining] = useState(null);
    const [suggestedQuestions, setSuggestedQuestions] = useState([]);
    const [showEscalation, setShowEscalation] = useState(false);
    const [summary, setSummary] = useState(null);
    const [summaryUpdatedAt, setSummaryUpdatedAt] = useState(null);
    const [showSummary, setShowSummary] = useState(false);
    const [regeneratingSummary, setRegeneratingSummary] = useState(false);
    const scrollRef = useRef(null);

    const loadSummary = useCallback(async () => {
        try {
            const r = await api.get('/seller/ai-chat/summary');
            setSummary(r.data?.summary || null);
            setSummaryUpdatedAt(r.data?.updated_at || null);
        } catch (_e) { /* silent */ }
    }, []);

    // Load history + summary when opening
    useEffect(() => {
        if (!open) return;
        const loadHistory = async () => {
            try {
                if (sessionId) {
                    const r = await api.get(`/seller/ai-chat/history?session_id=${sessionId}`);
                    if (r.data?.messages?.length) {
                        setMessages(r.data.messages);
                    }
                }
                const q = await api.get('/seller/ai-chat/quota');
                setQuotaRemaining(q.data?.remaining ?? null);
                const sq = await api.get('/seller/ai-chat/suggested-questions');
                setSuggestedQuestions(sq.data?.questions || []);
                await loadSummary();
            } catch (e) {
                /* silent */
            }
        };
        loadHistory();
    }, [open, sessionId, loadSummary]);

    // Scroll to bottom on new message
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, sending]);

    const sendMessage = useCallback(async (text) => {
        const question = (text || input).trim();
        if (!question || sending) return;
        setInput('');
        setSending(true);
        // Optimistic user message
        const userMsg = { role: 'user', content: question, created_at: new Date().toISOString() };
        setMessages((m) => [...m, userMsg]);
        try {
            const payload = { question, mandate_id: mandateId, session_id: sessionId };
            const r = await api.post('/seller/ai-chat/message', payload);
            if (r.data?.session_id && r.data.session_id !== sessionId) {
                setSessionId(r.data.session_id);
                localStorage.setItem(STORAGE_SESSION_KEY, r.data.session_id);
            }
            const aiMsg = {
                role: 'assistant',
                content: r.data?.answer || '',
                is_sensitive: r.data?.is_sensitive,
                created_at: new Date().toISOString(),
            };
            setMessages((m) => [...m, aiMsg]);
            setQuotaRemaining(r.data?.quota_remaining ?? null);
            if (r.data?.is_sensitive) setShowEscalation(true);
        } catch (e) {
            if (e.response?.status === 429) {
                toast.error('Quota quotidien atteint (30 questions/jour). Reprenez demain.');
            } else {
                toast.error("Erreur d'envoi. Réessayez.");
            }
            // Remove optimistic message on failure
            setMessages((m) => m.slice(0, -1));
        } finally {
            setSending(false);
        }
    }, [input, sending, mandateId, sessionId]);

    const resetSession = () => {
        if (!window.confirm('Démarrer une nouvelle conversation ?\n\nNe vous inquiétez pas : le résumé global garde la trace de tout ce qui a été dit.')) return;
        setMessages([]);
        setSessionId(null);
        localStorage.removeItem(STORAGE_SESSION_KEY);
        setShowEscalation(false);
        // Forcer la régénération du résumé pour figer la trace avant de recommencer
        regenerateSummary();
    };

    const regenerateSummary = useCallback(async () => {
        setRegeneratingSummary(true);
        try {
            const r = await api.post('/seller/ai-chat/summary/regenerate');
            setSummary(r.data?.summary || null);
            setSummaryUpdatedAt(r.data?.updated_at || null);
        } catch (_e) { /* silent */ } finally {
            setRegeneratingSummary(false);
        }
    }, []);

    // Floating button when closed
    if (!open) {
        return (
            <motion.button
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setOpen(true)}
                className="fixed bottom-24 sm:bottom-5 right-5 z-50 flex items-center gap-2.5 pl-4 pr-5 py-3.5 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-2xl shadow-[#681793]/50 font-bold hover:scale-105 transition-all ring-4 ring-[#FF914D]/20"
                data-testid="seller-ai-chat-fab"
            >
                {/* Pulse ring animation around the entire button */}
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] animate-ping opacity-30" />
                <div className="relative flex items-center gap-2.5">
                    <div className="relative">
                        <Sparkles className="w-5 h-5" />
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse ring-2 ring-[#0a0a1a]" />
                    </div>
                    <span className="text-sm whitespace-nowrap">Demander à l'IA</span>
                </div>
            </motion.button>
        );
    }

    const containerClass = fullscreen
        ? 'fixed inset-0 z-50'
        : 'fixed bottom-16 right-0 sm:bottom-6 sm:right-6 z-40 w-full sm:w-[420px] h-[75vh] sm:h-[600px] sm:rounded-2xl';

    return (
        <AnimatePresence>
            <motion.div
                key="chat-panel"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 30 }}
                transition={{ duration: 0.25 }}
                className={`${containerClass} bg-[#0a0a1a] sm:border border-white/10 shadow-2xl overflow-hidden flex flex-col`}
                data-testid="seller-ai-chat-panel"
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-[#681793] to-[#FF914D] px-4 py-3 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-white font-bold text-sm">Assistant IA Homly</p>
                            <p className="text-white/75 text-[10px] flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />
                                Propulsé par Confident · IA Homly
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {(messages.length > 0 || summary) && (
                            <button
                                onClick={() => setShowSummary((s) => !s)}
                                className="w-8 h-8 rounded-lg hover:bg-white/15 flex items-center justify-center text-white"
                                title={showSummary ? 'Masquer le résumé' : 'Voir le résumé global'}
                                data-testid="chat-toggle-summary"
                            >
                                <History className="w-4 h-4" />
                            </button>
                        )}
                        {messages.length > 0 && (
                            <button onClick={resetSession} className="w-8 h-8 rounded-lg hover:bg-white/15 flex items-center justify-center text-white" title="Nouvelle conversation (résumé conservé)" data-testid="chat-reset">
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        )}
                        <button onClick={() => setFullscreen((f) => !f)} className="w-8 h-8 rounded-lg hover:bg-white/15 flex items-center justify-center text-white hidden sm:flex" title={fullscreen ? 'Réduire' : 'Agrandir'}>
                            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </button>
                        <button onClick={() => { setOpen(false); setFullscreen(false); }} className="w-8 h-8 rounded-lg hover:bg-white/15 flex items-center justify-center text-white" data-testid="chat-close">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Panneau Résumé global */}
                <AnimatePresence>
                    {showSummary && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="bg-gradient-to-b from-[#1a0b2e] to-[#0f0820] border-b border-white/10 overflow-hidden"
                            data-testid="chat-summary-panel"
                        >
                            <div className="px-4 py-3.5">
                                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                    <div className="flex items-center gap-1.5">
                                        <Sparkles className="w-3.5 h-3.5 text-[#FF914D]" />
                                        <p className="text-[10px] uppercase tracking-widest text-[#FF914D] font-bold">
                                            Mémoire de nos échanges
                                        </p>
                                    </div>
                                    <button
                                        onClick={regenerateSummary}
                                        disabled={regeneratingSummary}
                                        className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10 transition-all disabled:opacity-50"
                                        data-testid="chat-regen-summary"
                                    >
                                        {regeneratingSummary ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                        Mettre à jour
                                    </button>
                                </div>
                                {summary ? (
                                    <div className="text-[12px] text-white/80 leading-relaxed whitespace-pre-line max-h-44 overflow-y-auto pr-1">
                                        {summary}
                                    </div>
                                ) : (
                                    <p className="text-[11px] text-white/40 italic">
                                        Posez quelques questions et un résumé apparaîtra ici — il garde une trace de tout pour ne rien oublier.
                                    </p>
                                )}
                                {summaryUpdatedAt && (
                                    <p className="text-[9px] text-white/30 mt-2">
                                        Mis à jour le {new Date(summaryUpdatedAt).toLocaleDateString('fr-FR')}
                                    </p>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#0a0a1a]">
                    {messages.length === 0 && (
                        <div className="text-center py-6">
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#681793]/40 to-[#FF914D]/30 border border-white/10 flex items-center justify-center mx-auto mb-3">
                                <Sparkles className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-white font-bold text-base mb-1.5">Posez vos questions</h3>
                            <p className="text-white/55 text-xs leading-relaxed max-w-xs mx-auto mb-5">
                                L'IA répond instantanément sur l'avancée de votre mandat, les visites, le marché et plus encore.
                            </p>
                            <div className="space-y-2">
                                {suggestedQuestions.map((q, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => sendMessage(q)}
                                        className="w-full text-left px-3.5 py-2.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#FF914D]/30 rounded-lg text-xs text-white/80 transition-all"
                                        data-testid={`suggested-question-${i}`}
                                    >
                                        💬 {q}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {messages.map((m, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            data-testid={`chat-message-${m.role}-${i}`}
                        >
                            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                                m.role === 'user'
                                    ? 'bg-gradient-to-br from-[#681793] to-[#5a1080] text-white rounded-br-sm'
                                    : m.is_sensitive
                                        ? 'bg-amber-500/10 border border-amber-500/30 text-white rounded-bl-sm'
                                        : 'bg-white/[0.06] border border-white/10 text-white/95 rounded-bl-sm'
                            }`}>
                                {m.is_sensitive && (
                                    <div className="flex items-center gap-1.5 mb-1.5 text-amber-300 text-[10px] font-bold uppercase tracking-wider">
                                        <AlertTriangle className="w-3 h-3" />
                                        Sujet sensible
                                    </div>
                                )}
                                <div className="whitespace-pre-wrap">{m.content}</div>
                            </div>
                        </motion.div>
                    ))}

                    {sending && (
                        <div className="flex justify-start">
                            <div className="bg-white/[0.06] border border-white/10 rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2">
                                <Loader2 className="w-3.5 h-3.5 text-white/60 animate-spin" />
                                <span className="text-xs text-white/60">L'IA réfléchit…</span>
                            </div>
                        </div>
                    )}

                    {showEscalation && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="rounded-xl bg-white/[0.03] border border-amber-500/20 p-3 mt-2"
                        >
                            <p className="text-xs text-white/70 mb-2.5 leading-relaxed">
                                <strong className="text-white">Sujet sensible détecté.</strong> Pour ce type de question, contactez votre support humain.
                            </p>
                            <Link
                                to="/seller/support"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 text-xs font-bold transition-colors"
                                data-testid="chat-escalation-ticket"
                            >
                                <Lock className="w-3 h-3" />
                                Créer un ticket support
                            </Link>
                            <button onClick={() => setShowEscalation(false)} className="ml-2 text-white/40 text-xs hover:text-white/70">Plus tard</button>
                        </motion.div>
                    )}
                </div>

                {/* Input */}
                <div className="border-t border-white/10 px-3 py-3 bg-[#0a0a1a] shrink-0">
                    <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex items-center gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Posez votre question…"
                            className="flex-1 px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF914D]/40 focus:ring-2 focus:ring-[#FF914D]/20"
                            disabled={sending}
                            data-testid="chat-input"
                        />
                        <button
                            type="submit"
                            disabled={sending || !input.trim()}
                            className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#681793] to-[#FF914D] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 transition-transform"
                            data-testid="chat-send"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                    <div className="flex items-center justify-between mt-2 text-[10px] text-white/35">
                        <span className="flex items-center gap-1">
                            <Lock className="w-2.5 h-2.5" />
                            Conversations confidentielles
                        </span>
                        {quotaRemaining !== null && (
                            <span data-testid="chat-quota">
                                {quotaRemaining} question{quotaRemaining > 1 ? 's' : ''} restante{quotaRemaining > 1 ? 's' : ''} aujourd'hui
                            </span>
                        )}
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
